import React, {useContext, useEffect, useState} from "react";
import { useTranslation, Trans } from "react-i18next";

//MainTemplate
import MainTemplate from "./MainTemplate";

//scss
import "../scss/common.scss";
import "../scss/component.scss";

//パーツをインポート
import { PartsStep, PartsCancellationChargeText, PartsTutorialButton } from "./parts";

import { SectionDetail, SectionCheck } from "./section/PlanSelectComplete";
import { PlanSelectFormData } from "../types/planSelectForm";
import { useLocation, useNavigate } from "react-router-dom";
import { getPriceRequestParams } from "../utils/price";
import { useTravelPrice } from "../utils/useTravelPrice";
import { Backdrop, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SectionTravelPrice from "./section/SectionTravelPrice";
import { isPlanSelectComplete } from "../utils/errors";
import Page404 from "./Page404";
import {defaultPlanSelectForm} from "../defaults/palnSelectForm";
import {getBackSearchURL} from "../utils/search";
import SectionTravelDiscountDetail from "./section/OkinawaSaihakken/SectionTravelDiscountDetail";
import SectionRetry from "./SectionRetry";
import {getHeadTitle} from "../utils/title";

//モーダルをインポート
import { SupportRateApproveModal} from "./modal"
import _ from "lodash";
import {FormSessionContext} from "../hooks/FormSession";
import {TagManagerContext, TagManagerType} from "../hooks/GTM";
import {langPath} from "../utils/lang";

// lodingのスタイル
const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));


const PlanSelectComplete: React.FC = () => {
  const { t, i18n } = useTranslation();
  const tagManagerContext: TagManagerType = useContext(TagManagerContext);
  const {checkSession} = useContext(FormSessionContext);
  const classesForLoading = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [move, setMove] = useState(false);
  const planData = (location.state as { data: PlanSelectFormData }) ?? {data: defaultPlanSelectForm};
  const [planSelectFormData, setPlanSelectFormData] = useState<{data: PlanSelectFormData}>(planData); //TODO: need change
  const hidden_contents: string[] = [];
  if (planSelectFormData?.data?.planData?.sellerIndo?.rentalCarSettings?.status === "disabled") {
    hidden_contents.push("rentalCar");
  }

  useEffect(() => {
    window.scrollTo(0, 0);
    tagManagerContext.pageView(getHeadTitle(planSelectFormData.data, "旅程の最終確認"))
  }, []);

  /**
   * お客様情報入力ページに遷移
   */
  const handleMove = () => {
    const session = checkSession(planSelectFormData.data);
    if (
      session &&
      planSelectFormData &&
      planSelectFormData.data.hotel.length > 0 &&
      planSelectFormData.data.hotel[0].plan !== undefined
    ) {
      navigate(langPath(i18n.language) +"/form1", {
        state: {
          data: planSelectFormData.data,
        },
      });
    }
  };

  const _planSelectFormData = _.cloneDeep(planSelectFormData);
  _planSelectFormData.data.discounts = _planSelectFormData.data.discounts.map((discount) => {
    return {...discount, used: true};
  })
  const priceParams = getPriceRequestParams(_planSelectFormData.data, undefined, undefined);
  const [priceData, { isLoading, isError, error, retryAfterSec, retry }] = useTravelPrice({data: priceParams});

  /**
   * `/price` を組み立てられる状態か。 issue #2992 / 2026-09-07
   *
   * ★**リクエストパラメータが取れない**（プランID・検索条件・宿泊が無い）場合は、
   *   この画面で引き直しても直らないので従来どおりプランページへ戻す。
   *   それ以外（通信・上流障害・`status !== 200`）は**画面に留めて再読み込み**させる。
   *   混雑時に戻すと、そこから `/plan`・`/hotel`・`/air` をやり直すことになり、
   *   1人あたり約20回の上流コールが再発生する（2026-08-31 実測）。
   */
  const hasPriceParams = !!planSelectFormData?.data?.id &&
    !!planSelectFormData?.data?.searchParams?.start &&
    planSelectFormData.data.hotel.length > 0;

  /** 料金を取得できていない（＝この先へ進ませない） */
  const priceUnavailable = isError || (!!priceData && priceData.status !== 200);
  /**
   * 料金が確定しているか。 2026-09-07 再レビュー
   * ★「失敗していない」ではなく「**取得できている**」で判定する。
   *   取得前（`priceData` が undefined・`isError` も false）の一瞬だけ
   *   ボタンが押せてしまう穴があった。
   */
  const priceReady = priceData?.status === 200 && !isError;

  const [supportRateApproveModal, setSupportRateApproveModal] = React.useState(false);

  // 割引適用確認モーダル
  const handleSupportRateApproveModal = () => {
    // ★料金を取得できていないまま先へ進ませない（2026-09-07）
    if (!priceReady) {
      return;
    }
    if (priceData?.result?.discountPrices && priceData?.result?.discountPrices.length > 0) {
      setSupportRateApproveModal(!supportRateApproveModal)
    } else {
      handleMove();
    }
  }

  // モーダルの閉じるボタンをクリックした
  const handleCloseModal = () => {
    setSupportRateApproveModal(false);
  }

  const callbackDiscountApprove = (approve: boolean) => {
    const updatePlanSelectFormData = _.cloneDeep(planSelectFormData);
    updatePlanSelectFormData.data.discounts = updatePlanSelectFormData.data.discounts.map((discount) => {
      return {...discount, used: discount.key === "okinawaSaihakken" ? approve : discount.used};
    });
    setPlanSelectFormData(updatePlanSelectFormData);
    setMove(true);
  }

  useEffect(() => {
    if (priceData?.status === 200 && !isError) {
      planSelectFormData.data.travelPrice = priceData.result.insurance.insuranceCancelAmount;
      return;
    }
    // ★リクエストパラメータが取れない場合だけ従来どおり戻す（2026-09-07）。
    //   `/price` の失敗そのものは画面に留めて再読み込みさせる（下の SectionRetry）
    if (priceUnavailable && !hasPriceParams) {
      alert(t("料金処理でエラーが発生しました、申し訳ありませんが時間を置いて再度プランの設定をお願いします。"));
      window.location.href = getBackSearchURL(planSelectFormData.data.searchParams, planSelectFormData.data.id, i18n.language);
    }
  }, [priceData, isError, priceUnavailable, hasPriceParams]);

  useEffect(() => {
    if (move) {
      handleMove();
    }
  }, [planSelectFormData.data.discounts])

  if (!planSelectFormData || isPlanSelectComplete(planSelectFormData.data)) {
    return <Page404></Page404>;
  }
  return (
    <MainTemplate
      title={getHeadTitle(planSelectFormData.data, "旅程の最終確認")} 
      planSelectFormData={planSelectFormData.data}
    >
      {/* ローディングの表示(APIのレスポンス待ち) */}
      <Backdrop className={classesForLoading.backdrop} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className="wrap-content-900-no_padding">
        <PartsStep activeStep={3}></PartsStep>
      </div>

      {/* 操作ガイドボタン */}
      <PartsTutorialButton value="component-plan_select" />

      <div className="wrap-content-900">
        {/* <!--アラート--> */}
        <div className="box-border-blue small icon-alert mt-16-md">
          <p className="fz-15 fz-16-md c-blue fw-b">
            {t("選択した内容、旅行代金をご確認ください")}
          </p>
        </div>

        {/* /行程表 */}
        <SectionDetail planSelectFormData={planSelectFormData.data}></SectionDetail>

        {/* 全国旅行支援 補助金内訳 */}
        {priceData?.result?.discountPrices && (
          <SectionTravelDiscountDetail discountPrices={priceData.result.discountPrices}></SectionTravelDiscountDetail>
        )}

        {/* 料金内訳 */}
        {priceUnavailable && hasPriceParams && !isLoading ? (
          /*
           * ★料金を取得できなかった（issue #2992 / 2026-09-07）。
           *   従来は alert を出してプランページへ戻していたため、混雑時に戻された利用者が
           *   そこから検索をやり直し、上流コールが再発生していた。画面に留めて引き直させる。
           */
          <SectionRetry
            retryAfterSec={retryAfterSec}
            isLoading={isLoading}
            onRetry={() => retry()}
            message={t("料金を取得できませんでした。混雑している可能性があります。") ?? undefined}
            buttonLabel={t("料金を再取得する") ?? undefined}
          />
        ) : (
          <SectionTravelPrice priceData={priceData}></SectionTravelPrice>
        )}

        {/* キャンセル料について */}
        <PartsCancellationChargeText></PartsCancellationChargeText>

        {/* 確認事項 */}
        <SectionCheck tourConductor={planSelectFormData.data?.sellerProduct?.tourConductor ?? ""}></SectionCheck>

        {/* モーダル支援割：承諾モーダル */}
        <SupportRateApproveModal
          isShow={supportRateApproveModal}
          onClickClose={handleCloseModal}
          priceBeforeDiscount={priceData?.result.notDiscountPriceTotal ?? 0}
          priceAfterDiscount={priceData?.result.taxTotalPrice ?? 0}
          callbackDiscountApprove={callbackDiscountApprove}
        />

        {/* <!--次へ進む--> */}
        <div className="mt-48 mt-64-md">
          <ul className="form-step_button mt-24">
            <li className="form-step_button-next">
              {/* Form1へ進む */}
              <button
                type="button"
                className={priceReady ? "button-large-red" : "button-large-red button-medium-disabled"}
                disabled={!priceReady || isLoading}
                // onClick={handleMove}
                onClick={handleSupportRateApproveModal}
                style={{
                  display: "block",
                  border: "none",
                  width: "100%",
                }}
              >
                {t("お客様情報入力へ進む")}
              </button>
            </li>
            <li className="form-step_button-back">
              <button
                className="button-small-border_gray_blue button-medium-md"
                onClick={() => {
                  navigate(langPath(i18n.language) + "/planselectconfirm", { state: {
                      data: planSelectFormData.data
                    } })
                }}
              >
                <i className="icon-left_arrow mr-5"></i>
                {t("旅行日程の確認・変更へ戻る")}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </MainTemplate>
  );
};

export default PlanSelectComplete;
